import React, { useState, useRef, useEffect } from 'react';
import HeroRotator from './HeroRotator';

const MAIN_APP = process.env.REACT_APP_API_URL || '';
const PUBLIC_SITE = process.env.REACT_APP_PUBLIC_SITE_ORIGIN || 'https://www.ehandshake.com';

/* ─── Reusable pieces ─── */

function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center max-w-3xl mx-auto mb-6">
      {eyebrow && (
        <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-1">
          {eyebrow}
        </span>
      )}
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">{title}</h2>
      {subtitle && <p className="mt-2 text-gray-500 text-sm md:text-base leading-relaxed">{subtitle}</p>}
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="w-10 h-10 rounded-lg bg-[#00aef7]/10 flex items-center justify-center text-[#00aef7] mb-3">
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({ number, title, description }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00aef7] text-white flex items-center justify-center font-bold text-xs">
        {number}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-0.5">{title}</h3>
        <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function ScreenshotPlaceholder({ label }) {
  return (
    <div className="bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 aspect-video flex items-center justify-center">
      <div className="text-center">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-8 h-8 text-gray-300 mx-auto mb-1">
          <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
        </svg>
        <span className="text-[10px] text-gray-400 font-medium">{label}</span>
      </div>
    </div>
  );
}

function PlatformRow({ icon, bgColor, name, desc }) {
  return (
    <div className="flex gap-3 items-start">
      <div className={`w-8 h-8 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <h3 className="text-xs font-semibold text-gray-900">{name}</h3>
        <p className="text-[11px] text-gray-500">{desc}</p>
      </div>
    </div>
  );
}

/* ─── Design Center showcase videos served from CloudFront
 * (behavior /videos/* → S3 bucket ehandshake-public-assets/videos/designcenter/).
 * Override via REACT_APP_DESIGN_VIDEO_BASE_URL if the CDN ever moves. ── */
const DESIGN_VIDEO_BASE_URL =
  process.env.REACT_APP_DESIGN_VIDEO_BASE_URL ||
  'https://www.ehandshake.com/videos/designcenter';

/* ─── Design Center showcase steps (4 chapters: design → preview → landing → publish)
 * A 5th "sharing center" segment plays at the tail of the video but isn't a
 * card here — it acts as the visual handoff to the next homepage section.
 * Each step has a `videoFile` once its clip is cut + uploaded to S3; steps
 * without one fall back to the play-button placeholder. ── */
const DESIGN_CENTER_STEPS = [
  {
    key: 'design',
    label: 'AI-powered Design Center',
    title: 'Design your creation',
    description: 'AI suggests headlines, captions, and visuals tuned to your profession.',
    videoFile: 'DesignCenter1.mp4',
    benefits: [
      '8 idea categories to get your creative juices flowing.',
      'Design once — we format it natively for Facebook, Instagram, X, YouTube, LinkedIn, and TikTok.',
      'Fully describe your idea and let the eHandshake AI design it for you.',
      'Or tap "Inspirations" for fresh AI-generated ideas to post.',
      'Answer a few quick questions — your headline, per-platform captions, hashtags, and emoji are generated automatically.',
      '3.2 million royalty-free stock images and videos at your fingertips.',
      'Visuals tuned to your profession are surfaced first.',
      'Or upload your own photo or video.',
    ],
  },
  {
    key: 'preview',
    label: 'Preview Social Posts',
    title: 'Preview every platform',
    description: 'See how it lands on Facebook, Instagram, X, LinkedIn, TikTok, and YouTube — native format each.',
    videoFile: 'DesignCenter2.mp4',
    benefits: [
      'Fine-tune headline position on each platform for the best fit.',
      'Customize captions, hashtags, and emoji per platform so each post feels native.',
      'Turn static images into short videos for YouTube and TikTok posts.',
      'When every platform looks right, continue on to build the landing page your social links click through to.',
    ],
  },
  {
    key: 'landing',
    label: 'Create Landing Page',
    title: 'Create a landing page',
    description: 'A full mini-site for the creation — AI writes the copy, you approve.',
  },
  {
    key: 'publish',
    label: 'Publish Your Creation',
    title: 'Publish to the sharing center',
    description: 'Native-format posts go live on every platform you\'ve connected.',
  },
];

const videoUrlForStep = (step) =>
  step?.videoFile ? `${DESIGN_VIDEO_BASE_URL}/${step.videoFile}` : null;

/* ─── Design Center Showcase ───────────────────────────────────────────────
 * Apple-style layout: info text (left col) + cards row above video (right col).
 * Behavior:
 *   - activeIdx  = last-clicked card ("pinned"). Starts at 0.
 *   - hoverIdx   = currently hovered card. null when not hovering.
 *   - displayIdx = hoverIdx ?? activeIdx. Drives left-column text AND the
 *                  video-area thumbnail preview (before first play).
 *   - hasStarted = false on first load. Play button overlay shown until the
 *                  user clicks play OR any card.
 *   - After hasStarted=true, card clicks immediately swap to that segment.
 *     Hover while playing updates ONLY the left-column text; the video keeps
 *     playing its current segment.
 * ────────────────────────────────────────────────────────────────────────── */
function DesignCenterShowcase() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [hoverIdx,  setHoverIdx]  = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const videoRef = useRef(null);

  // Left column follows the hover if present, else the pinned (active) step.
  const displayIdx = hoverIdx !== null ? hoverIdx : activeIdx;

  // Thumbnail shown in the video area when nothing is playing.
  // Once playback has started, the stage is dominated by the active segment
  // regardless of what's hovered (hover only affects the left-column text).
  const thumbnailIdx = hasStarted ? activeIdx : displayIdx;

  const handleCardClick = (i) => {
    setActiveIdx(i);
    setHasStarted(true);
  };

  const displayStep   = DESIGN_CENTER_STEPS[displayIdx];
  const thumbnailStep = DESIGN_CENTER_STEPS[thumbnailIdx];
  const videoUrl      = videoUrlForStep(thumbnailStep);

  // Sync the <video> element to the current segment. Before hasStarted, setting
  // src shows the first-frame thumbnail (preload="metadata"). Once hasStarted
  // is true or activeIdx changes, play from the start of the new segment.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoUrl) return;
    // Avoid clobbering the stream if the src is already correct.
    const current = el.src.split('?')[0];
    if (current !== videoUrl) {
      el.src = videoUrl;
      el.load();
    }
    if (hasStarted) {
      el.currentTime = 0;
      el.play().catch(() => {});
    }
  }, [videoUrl, hasStarted]);

  return (
    <section className="bg-gray-50 border-y border-gray-100">
      <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-14">

        {/* Section header — brand-blue hero title above the 2×2 grid. */}
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#00aef7] tracking-tight text-center mb-10 md:mb-14">
          AI-powered Social Design Center
        </h2>

        {/* 2×2 grid. Row 1: left heading + arrow / right cards.
            Row 2: left step info (hover-driven) / right video.
            Columns line up so the heading sits beside the cards and the
            hover-driven info sits beside the video. Stacks on mobile. */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_820px] md:grid-rows-[auto_auto] gap-x-8 md:gap-x-10 gap-y-3 md:gap-y-4">

          {/* ─── ROW 1 / LEFT: "Easy 4 step process" heading + arrow ─── */}
          <div className="flex flex-col justify-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#00aef7] leading-[1.1] tracking-tight flex items-center gap-2 md:gap-3">
              <span className="whitespace-nowrap">Easy 4 step process</span>
              {/* Horizontal arrow pointing at the cards on the right. Hidden on
                  mobile since the cards stack below, not beside. */}
              <svg
                className="hidden md:block w-10 h-4 flex-shrink-0"
                viewBox="0 0 100 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M2 10 h 86 M80 2 l 10 8 l -10 8"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </h2>
            <p className="mt-2 text-gray-700 text-sm md:text-base leading-relaxed max-w-md">
              Design a creation and landing page in under 5 minutes with the help of our AI assistant.
            </p>
          </div>

          {/* ─── ROW 1 / RIGHT: 4 minimal step cards ─── */}
          <div className="grid grid-cols-4 gap-2">
            {DESIGN_CENTER_STEPS.map((step, i) => {
              const isActive = i === activeIdx;
              const isHover  = i === hoverIdx;
              return (
                <button
                  key={step.key}
                  type="button"
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                  onClick={() => handleCardClick(i)}
                  className={`px-1 py-2 rounded-lg border-2 bg-white transition-all flex flex-col items-center justify-center gap-1 ${
                    isActive
                      ? 'border-[#00aef7] shadow-md'
                      : isHover
                        ? 'border-gray-400 shadow-sm'
                        : 'border-gray-200'
                  }`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      isActive ? 'bg-[#00aef7] text-white' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <div
                    className={`text-[10px] uppercase tracking-wider font-bold text-center leading-tight ${
                      isActive ? 'text-[#00aef7]' : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </div>
                </button>
              );
            })}
          </div>

          {/* ─── ROW 2 / LEFT: step-dependent content.
              If the current step has a `benefits` array, show brand-blue-check
              bullets. Otherwise fall back to the original hover-driven title +
              description block. ─── */}
          <div className="flex items-center min-h-[160px] md:min-h-[360px]">
            {displayStep.benefits ? (
              <ul className="space-y-2.5 md:space-y-3 w-full max-w-md">
                {displayStep.benefits.map((text, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#00aef7] text-white flex items-center justify-center shadow-sm">
                      <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.997 8a1 1 0 01-1.414 0l-3.997-4a1 1 0 011.414-1.42L8 12.58l7.29-7.29a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span className="text-gray-700 text-xs md:text-sm leading-snug">
                      {text}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center max-w-sm px-2 mx-auto">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00aef7] mb-2">
                  Step {displayIdx + 1}
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-3">
                  {displayStep.title}
                </h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  {displayStep.description}
                </p>
              </div>
            )}
          </div>

          {/* ─── ROW 2 / RIGHT: video stage ─── */}
          <div>
            <div
              className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10 bg-gradient-to-br from-gray-900 via-gray-800 to-black"
              style={{ aspectRatio: '1280 / 608' }}
            >
              {/* Real <video> when this segment has a file uploaded. Before
                  hasStarted, preload="metadata" shows the first frame as
                  thumbnail. Rollovers change thumbnailIdx → useEffect swaps
                  src. Clicking a card or the play button sets hasStarted and
                  the effect calls play(). */}
              {videoUrl && (
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  preload="metadata"
                  controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                  disablePictureInPicture
                  disableRemotePlayback
                  onContextMenu={(e) => e.preventDefault()}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}

              {/* Dark scrim over the first frame until the user hits play. The
                  video still shows behind it (you can faintly see the source
                  frame), but the scrim dims it enough that the play button
                  has real contrast instead of fighting with blurry UI text. */}
              <div
                className={`absolute inset-0 bg-gradient-to-br from-black/70 via-black/75 to-black/70 transition-opacity duration-500 pointer-events-none ${
                  hasStarted && videoUrl ? 'opacity-0' : 'opacity-100'
                }`}
              />

              {/* Play button + label overlay. Shown before hasStarted, also
                  shown for segments with no video uploaded yet. Hidden only
                  when the video is actually playing a real file. */}
              <div
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                  hasStarted && videoUrl ? 'opacity-0 pointer-events-none' : 'opacity-100'
                }`}
              >
                <div className="text-center">
                  {!hasStarted && (
                    <button
                      type="button"
                      onClick={() => handleCardClick(thumbnailIdx)}
                      className="group w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#00aef7] hover:bg-[#0098d8] shadow-xl flex items-center justify-center mx-auto mb-3 transition-all hover:scale-105"
                      aria-label="Play walkthrough"
                      disabled={!videoUrl}
                    >
                      <svg className="w-7 h-7 md:w-9 md:h-9 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </button>
                  )}
                  <div className="text-white text-xs md:text-sm font-medium mt-1">
                    {thumbnailStep.label}
                  </div>
                  {!videoUrl && (
                    <div className="text-white/50 text-[10px] mt-1">
                      Video coming soon
                    </div>
                  )}
                </div>
              </div>

              {/* Fullscreen toggle — visible once the video is playing. Uses the
                  browser's native requestFullscreen so the user can expand the
                  walkthrough to the full display when they want a better look. */}
              {hasStarted && videoUrl && (
                <button
                  type="button"
                  onClick={() => {
                    const el = videoRef.current;
                    if (!el) return;
                    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
                    if (req) req.call(el).catch(() => {});
                  }}
                  className="absolute bottom-3 right-3 w-9 h-9 rounded-lg bg-black/50 hover:bg-black/75 backdrop-blur-sm flex items-center justify-center text-white transition opacity-80 hover:opacity-100"
                  aria-label="Expand to fullscreen"
                  title="Fullscreen"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
                  </svg>
                </button>
              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ─── */

const Home = () => {
  return (
    <main className="flex-grow bg-white">

      {/* ══════════ HERO ══════════ */}
      <HeroRotator />


      {/* ══════════ EMPATHY STORY — 9 PM vignette ══════════ */}
      <section className="bg-[#faf7f2] border-y border-stone-200">
        <div className="mx-auto w-[90%] max-w-3xl py-14 md:py-20">
          <div className="text-center mb-10">
            <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-3">
              The Problem Every Small Business Owner Knows
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              It&rsquo;s 9 PM.
            </h2>
          </div>

          <div className="font-serif text-gray-700 text-base md:text-lg leading-relaxed space-y-5">
            <p>
              The lights are off in the shop. You sit down with a cup of coffee
              and decide: tonight, I&rsquo;m going to finally post something on
              social media.
            </p>
            <p>
              You open Instagram. <em>What do I say?</em> You open Facebook.{' '}
              <em>What photo do I use?</em> You try Canva. You freeze. You google{' '}
              &ldquo;how to post as a plumber&rdquo; and get overwhelmed. An
              hour later, you close the laptop and go to bed.
            </p>
            <p>
              Tomorrow you&rsquo;ll be on a job site at 7 AM. Then another. Then
              another. Marketing will wait.
            </p>
          </div>

          {/* Photo storyboard — 9 PM → 10 PM → 11 PM → 7 AM */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {[
              { time: '9 PM',  label: 'Sit down with good intentions', src: '/images/empathy/empathy-9pm-coffee.jpg',   alt: 'Coffee mug on a home kitchen table in the evening, laptop nearby' },
              { time: '10 PM', label: 'The idea repository is empty',     src: '/images/empathy/empathy-10pm-stuck.jpg',    alt: 'Face lit by a laptop screen, thinking, frustrated' },
              { time: '11 PM', label: 'Close the laptop. It can wait.',    src: '/images/empathy/empathy-11pm-closing.jpg',  alt: 'Laptop being closed in a dimly lit room late at night' },
              { time: '7 AM',  label: 'Back on the job. Again.',       src: '/images/empathy/empathy-7am-jobsite.jpg',   alt: 'Tradesman starting work at a job site in the early morning' },
            ].map(({ time, label, src, alt }) => (
              <figure key={time} className="text-center">
                <div className="overflow-hidden rounded-lg shadow-md ring-1 ring-stone-200/70">
                  <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    className="w-full aspect-[3/2] object-cover"
                  />
                </div>
                <figcaption className="mt-3">
                  <div className="text-sm font-semibold text-gray-800 tracking-wide">{time}</div>
                  <div className="text-[11px] md:text-xs text-gray-500 mt-0.5 leading-snug">
                    {label}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-14 pt-10 border-t border-stone-200 font-serif text-gray-800 text-base md:text-lg leading-relaxed space-y-4">
            <p>
              We built eHandshake because this happens to every single owner or salesperson in
              millions of small businesses.
            </p>
            <p className="font-semibold text-gray-900">
              You&rsquo;re not bad at marketing. You&rsquo;re a specialist at
              what you do, and marketing is its own specialty. You shouldn&rsquo;t
              have to become two professionals.
            </p>
          </div>
        </div>
      </section>


      {/* ══════════ DESIGN CENTER SHOWCASE — interactive cards + video ══════════ */}
      <DesignCenterShowcase />


      {/* ══════════ SHARING CENTER ══════════ */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-12">
          <SectionHeading
            eyebrow="Step 2: Publish"
            title="One Creation. Every Platform."
            subtitle="Design once. We format it native for Facebook, Instagram, X, YouTube, LinkedIn, and TikTok — preview every version before you publish, then post each one from a single workspace. What looks right on each platform is what gets published to each platform."
          />
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <ScreenshotPlaceholder label="Sharing Center screenshot" />
              <p className="mt-2 text-center text-[10px] text-gray-400">The Sharing Center — publish and track engagement in one place</p>
            </div>
            <div className="space-y-4">
              <PlatformRow
                icon={<svg viewBox="0 0 24 24" className="w-4 h-4 text-blue-600" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>}
                bgColor="bg-blue-500/10"
                name="Facebook & Instagram"
                desc="Post photos and videos directly to your business page and Instagram. Track likes, comments, shares, and views."
              />
              <PlatformRow
                icon={<svg viewBox="0 0 24 24" className="w-4 h-4 text-gray-800" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>}
                bgColor="bg-gray-900/10"
                name="X (Twitter)"
                desc="Publish tweets with images and track engagement metrics."
              />
              <PlatformRow
                icon={<svg viewBox="0 0 24 24" className="w-4 h-4 text-blue-700" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z" /></svg>}
                bgColor="bg-blue-700/10"
                name="LinkedIn"
                desc="Share professional content to your personal LinkedIn page to build industry credibility."
              />
              <PlatformRow
                icon={<svg viewBox="0 0 24 24" className="w-4 h-4 text-red-600" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>}
                bgColor="bg-red-500/10"
                name="YouTube & TikTok"
                desc="Upload video content with AI-generated captions optimized for each platform."
              />
              <PlatformRow
                icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-[#00aef7]"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" /></svg>}
                bgColor="bg-[#00aef7]/10"
                name="Engagement Tracking"
                desc="See likes, comments, shares, and views for every post — across every platform — in one dashboard."
              />
            </div>
          </div>
        </div>
      </section>


      {/* ══════════ TEAMS ══════════ */}
      <section>
        <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-12">
          <SectionHeading
            eyebrow="Step 3: Grow Together"
            title="Team Up. Cross-Promote. Win."
            subtitle="Join a team of local businesses. When one teammate creates a post, everyone can share it — multiplying reach without multiplying effort."
          />
          <div className="grid md:grid-cols-3 gap-4">
            <FeatureCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>}
              title="Build Your Team"
              description="Invite complementary local businesses — the roofer, the electrician, the HVAC tech. Support each other's marketing with zero extra work."
            />
            <FeatureCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" /></svg>}
              title="Share Each Other's Content"
              description="When your teammate posts a tip about water heaters, you can share it to your audience — and they'll do the same for you."
            />
            <FeatureCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" /></svg>}
              title="Collective Reputation"
              description="Your team score combines everyone's reviews. A strong team means more trust, more visibility, and more customers for all."
            />
          </div>
        </div>
      </section>


      {/* ══════════ HOW IT WORKS ══════════ */}
      <section className="bg-gradient-to-br from-[#00aef7] to-[#0088cc] text-white">
        <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-12">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h2 className="text-xl md:text-2xl font-bold">From Zero to Published in 5 Minutes</h2>
            <p className="mt-2 text-white/80 text-sm">No marketing degree. No design skills. If you can tap a screen, you can market your business.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Sign Up', desc: 'Create your free account in 30 seconds.' },
              { step: '2', title: 'Pick an Idea', desc: 'Browse content ideas tailored to your trade.' },
              { step: '3', title: 'We Build It', desc: 'AI generates headline, captions, and visuals.' },
              { step: '4', title: 'Publish', desc: 'Native-formatted for each platform. You choose which ones go live.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold">{item.step}</span>
                </div>
                <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-white/75 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ══════════ TECH PROVIDER ══════════ */}
      <section>
        <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-12">
          <SectionHeading
            eyebrow="Technology Platform"
            title="Built for Businesses. Powered by Integrations."
            subtitle="eHandshake is a social media management platform that connects to Facebook, Instagram, X, YouTube, LinkedIn, and TikTok via official APIs. We publish content on behalf of our users with their explicit authorization through OAuth."
          />
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">How We Access User Data</h3>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">1.</span> User signs up and connects social accounts via OAuth</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">2.</span> We request only needed permissions: page publishing, media upload, engagement metrics</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">3.</span> User creates or selects content in our Design Center</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">4.</span> We publish to their connected accounts using official platform APIs</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">5.</span> We fetch engagement data (likes, comments, shares, views) to show performance</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Platform & Security</h3>
              <ul className="space-y-1.5 text-xs text-gray-600">
                {[
                  'OAuth 2.0 authentication — we never store passwords',
                  'SSL/TLS encryption on all connections',
                  'Users control which accounts are connected',
                  'Data deletion available on request',
                  'Hosted on AWS with automated backups',
                  'Real-time Matrix messaging (end-to-end encrypted)',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" /></svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* ══════════ CTA ══════════ */}
      <section className="bg-gray-900">
        <div className="mx-auto w-[90%] max-w-4xl py-10 md:py-14 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-white leading-tight">
            Stop struggling with marketing.<br />Start growing your business.
          </h2>
          <p className="mt-3 text-gray-400 text-sm max-w-xl mx-auto">
            Join hundreds of local professionals who use eHandshake to create professional content, publish to every platform, and build their reputation &mdash; without the headache.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <a
              href="/?modal=signup"
              className="inline-flex items-center px-6 py-3 rounded-lg bg-[#00aef7] text-white text-sm font-semibold hover:bg-[#0098d8] transition-colors shadow-lg shadow-[#00aef7]/25"
            >
              Get Started Free
            </a>
            <a
              href={`${PUBLIC_SITE}/search`}
              className="inline-flex items-center px-6 py-3 rounded-lg border border-gray-600 text-gray-300 text-sm font-semibold hover:bg-gray-800 transition-colors"
            >
              Browse Directory
            </a>
          </div>
        </div>
      </section>

    </main>
  );
};

export default Home;
